import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminService } from "../../../services/admin/admin.service";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { ArenaLogo } from "@/components/common/ArenaLogo";
import toast from "react-hot-toast";

export function SalesResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }

    setIsLoading(true);

    try {
      await adminService.salesResetPassword(token, newPassword);
      toast.success("Password set successfully! You can now log in.");
      navigate("/auth/sales/login");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to reset password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="md:m-auto mx-auto w-[98%] md:w-max pt-4 md:pt-0 md:h-max">
      <div className="h-full mx-auto flex flex-col items-center justify-center md:px-5 md:py-7 py-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100] dark:shadow-none">
        <ArenaLogo className="w-12 mb-8 border border-black mx-auto rounded-[12px] bg-[#f6f6f6]" />
        <div className="w-full flex items-center justify-center px-6">
          <div className="w-full">
            <div className="mb-8">
              <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center align-middle">
                Set Your Password
              </h1>
              <p className="font-normal text-[14px] mt-4 tracking-normal text-center align-middle text-muted-foreground">
                Choose a strong password for your sales account
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4 pb-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setNewPassword(e.target.value)
                  }
                  placeholder="At least 8 characters"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(e.target.value)
                  }
                  placeholder="Re-enter your password"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#d7785b] hover:bg-[#d7785b]/90"
                disabled={isLoading}
              >
                {isLoading ? "Setting password..." : "Set Password"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
